import { classNames } from '@fullstory/util/fmt';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { identifyComponent } from '../../util';
import { Fade } from '../Fade';
import css from './index.module.scss';

// TODO(craigmccown): Remove `useOverlayPortal` and associated context/types. We shouldn't need to
// make a special case for scroll behavior if we portal everything to the body. But, we currently
// need to because the body has a height of 0 because the AppView is absolutely positioned. See
// https://github.com/cowpaths/mn/pull/36603#issuecomment-911747183 for additional information.
type PortalFunc = (children: React.ReactNode) => React.ReactPortal | null;

const defaultPortalFunc: PortalFunc = children => ReactDOM.createPortal(children, document.body);

const OverlayContext = createContext<PortalFunc>(defaultPortalFunc);

/**
 * This hook provides a portal function to be used instead of ReactDOM.createPortal. By default, it
 * will return a function that portals to the document body. However, if this hook is called within
 * an overlay, it will return a function that portals to the overlay container. This ensures proper
 * scroll behavior for portaled content within overlays.
 */
export const useOverlayPortal = () => useContext(OverlayContext);

export type CloseReason = 'escape' | 'mask';

export interface OverlayProps {
  testId?: string;
  transparent?: boolean;
  open: boolean;
  onClose: (Reason: CloseReason) => void;
  onClosed?: () => void;
  overflowY?: 'auto' | 'hidden';
}

/**
 * Provides a backdrop component that has onClick handlers that call onClose when clicked.
 *
 * Note: The children should be absolutely positioned.
 */
export const Overlay: React.FC<PropsWithChildren<OverlayProps>> = ({
  testId = 'overlay',
  transparent = false,
  children,
  onClose,
  onClosed,
  open,
  overflowY = 'auto',
  ...rest
}) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const portalToContainer = useCallback<PortalFunc>(
    (children: React.ReactNode) => {
      if (containerEl == null) return null;

      return ReactDOM.createPortal(children, containerEl);
    },
    [containerEl],
  );
  const maskRef = useRef<HTMLDivElement>(null);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose('escape');
    },
    [onClose],
  );

  const previousScrollPosition = useRef({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (open) {
      const html = document.body.parentElement;
      const body = document.body;
      body.style.setProperty('overflow', 'hidden');

      // sets an 'empty' scrollbar on the html element if needed, removing page jitter
      if (html && html.scrollHeight > html.clientHeight) {
        // setting to fixed causes page to jump to top, save the current scroll
        previousScrollPosition.current = {
          top: body.scrollTop || window.scrollY || html.scrollTop || 0,
          left: body.scrollLeft || window.scrollX || html.scrollLeft || 0,
        };

        body.style.setProperty('height', '100dvh');
        html.style.setProperty('overflow-y', 'scroll');
        body.scrollTop = previousScrollPosition.current.top;
        body.scrollLeft = previousScrollPosition.current.left;
      }
    }

    return () => {
      if (open) {
        const html = document.body.parentElement;
        const body = document.body;
        body.style.removeProperty('overflow');
        body.style.removeProperty('height');

        if (html) {
          html.style.removeProperty('overflow-y');

          // restore scroll position
          window.scroll(previousScrollPosition.current.left, previousScrollPosition.current.top);
          previousScrollPosition.current = { top: 0, left: 0 };
        }
      }
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (open) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [handleKeyDown, open]);

  const handleMaskClick = () => onClose('mask');

  return ReactDOM.createPortal(
    <OverlayContext.Provider value={portalToContainer}>
      <Fade data-component="Overlay" in={open} onTransitionEnd={onClosed} nodeRef={maskRef}>
        <div
          ref={setContainerEl}
          className={classNames(css.container, {
            [css.hidden]: overflowY === 'hidden',
          })}
          {...identifyComponent('overlay')}
          {...rest}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            data-testid={testId}
            ref={maskRef}
            className={classNames(css.overlay, {
              [css.transparent]: transparent,
            })}
            onMouseDown={handleMaskClick}
            onTouchStart={handleMaskClick}
          />
          {children}
        </div>
      </Fade>
    </OverlayContext.Provider>,
    document.body,
  );
};
